import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Alert,
  Skeleton,
  Button,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Note as NoteIcon,
  Sms as SmsIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';

const TYPE_ICONS = {
  Email: EmailIcon,
  Note: NoteIcon,
  SMS: SmsIcon,
  Call: PhoneIcon,
};

const mapCommToTimelineItem = (comm) => {
  const typeName = comm.communication_type_name || comm.communication_type?.name || '';
  let type = 'Note';
  if (typeName === 'Email') type = 'Email';
  else if (typeName === 'Note') type = 'Note';
  else if (typeName === 'SMS' || typeName === 'sms') type = 'SMS';
  else if (['Phone Call', 'Meeting', 'Appointment'].includes(typeName)) type = 'Call';

  const content = (comm.content || comm.subject || '').replace(/<[^>]*>/g, '').trim();
  const preview = content.length > 80 ? content.substring(0, 80) + '…' : content || '(No content)';
  const name = comm.created_by_name || comm.client_name || 'Unknown';
  const avatarLetter = (name || 'U')[0].toUpperCase();
  const time = comm.created_at || comm.date_created;

  return {
    id: `comm-${comm.id}`,
    type,
    name,
    preview,
    time,
    avatarLetter,
    raw: comm,
    source: 'communication',
  };
};

const mapSmsToTimelineItem = (msg, clientName) => {
  const isOutbound = msg.direction === 'outbound' || msg.direction === 'out' || msg.from_me;
  const content = msg.body ?? msg.message ?? msg.content ?? '';
  const preview = content.length > 80 ? content.substring(0, 80) + '…' : content || '(No content)';
  const name = isOutbound ? 'You' : (clientName || 'Client');
  const avatarLetter = (name || 'U')[0].toUpperCase();
  const time = msg.timestamp ?? msg.created_at ?? msg.date_sent;

  return {
    id: `sms-${msg.id ?? msg.sid ?? Math.random()}`,
    type: 'SMS',
    name,
    preview,
    time,
    avatarLetter,
    raw: msg,
    source: 'sms',
  };
};

const formatTime = (dateString) => {
  const d = new Date(dateString);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const isThisYear = d.getFullYear() === now.getFullYear();
  return isThisYear ? d.toLocaleDateString([], { month: 'short', day: 'numeric' }) : d.toLocaleDateString();
};

const MessagesTimeline = ({ clientId, clientName, onCommunicationSelect, onSendFirstMessage, refreshTrigger }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError(null);
      const [commRes, smsRes] = await Promise.all([
        api.get(`/api/clients/${clientId}/communications/`).catch(() => ({ data: { communications: [] } })),
        enhancedApi.getSMSHistory(clientId).catch(() => ({ data: { messages: [] } })),
      ]);

      const comms = commRes.data?.communications ?? [];
      const smsList = Array.isArray(smsRes.data?.messages ?? smsRes.data)
        ? (smsRes.data?.messages ?? smsRes.data)
        : [];

      const commItems = comms.map((c) => mapCommToTimelineItem(c));
      const smsItems = smsList.map((m) => mapSmsToTimelineItem(m, clientName));

      const merged = [...commItems, ...smsItems].sort((a, b) => {
        const ta = new Date(a.time || 0).getTime();
        const tb = new Date(b.time || 0).getTime();
        return tb - ta;
      });

      setItems(merged);
    } catch {
      setError('Failed to load communications');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, clientName]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handleItemClick = (item) => {
    if (item.source === 'communication' && onCommunicationSelect) {
      onCommunicationSelect(item.raw, 'reply');
    }
  };

  const TypeIcon = ({ type }) => {
    const Icon = TYPE_ICONS[type] || NoteIcon;
    return <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />;
  };

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 1, flex: 1, minHeight: 200 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="90%" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', px: 2 }}>
            <ForumIcon sx={{ fontSize: 64, color: 'action.disabled', display: 'block', mx: 'auto', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start the conversation from here.
            </Typography>
            {onSendFirstMessage && (
              <Button variant="contained" onClick={onSendFirstMessage} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Send first message
              </Button>
            )}
          </Box>
        ) : (
          <List dense disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleItemClick(item)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                    {item.avatarLetter}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      <TypeIcon type={item.type} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                        {item.preview}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(item.time)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default MessagesTimeline;
